import { CheckCircle2, ShoppingBag } from 'lucide-react'

import { useEffect, useRef, useState } from 'react'

import { DisplayMessage, openDisplayChannel } from '@/services/customerDisplay'
import { formatMoney } from '@/utils'

type Shown = Exclude<DisplayMessage, { type: 'hello' }>

/** The screen facing the customer: what is being rung up, then the total and change. Read-only, fed by the till. */
export const CustomerDisplayPage = () => {
  const [shown, setShown] = useState<Shown | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    document.title = 'Customer display'
    const channel = openDisplayChannel()
    if (!channel) return
    channel.onmessage = (e: MessageEvent<DisplayMessage>) => e.data?.type !== 'hello' && setShown(e.data)
    channel.postMessage({ type: 'hello' } satisfies DisplayMessage)
    return () => channel.close()
  }, [])

  // The newest line is at the bottom; keep it in view.
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [shown])

  const shop = shown?.shop

  if (shown?.type === 'paid')
    return (
      <div className="grid h-dvh place-items-center bg-page p-8 text-center">
        <div className="grid justify-items-center gap-4">
          <CheckCircle2 className="size-20 text-success" />
          <p className="text-heading-lg font-semibold text-ink">Thank you!</p>
          <p className="text-lg text-text-2">Paid {formatMoney(shown.total)}</p>
          {shown.change > 0 && (
            <div className="rounded-md bg-success-lighter/60 px-10 py-6">
              <p className="text-sm font-medium uppercase tracking-wide text-success-dark">Your change</p>
              {/* eslint-disable-next-line no-restricted-syntax -- read from across the counter; larger than any token */}
              <p className="text-[4rem] font-semibold leading-tight tabular-nums text-success-darker">{formatMoney(shown.change)}</p>
            </div>
          )}
          {shop && <p className="text-text-2">{shop}</p>}
        </div>
      </div>
    )

  if (!shown || !shown.lines.length)
    return (
      <div className="grid h-dvh place-items-center bg-page p-8 text-center">
        <div className="grid justify-items-center gap-3">
          <ShoppingBag className="size-16 text-muted-foreground" />
          <p className="text-heading-lg font-semibold text-ink">{shop ? `Welcome to ${shop}` : 'Welcome'}</p>
        </div>
      </div>
    )

  const t = shown.totals
  const discount = t.lineDiscounts + t.orderDiscount
  return (
    <div className="grid h-dvh grid-rows-[auto_1fr_auto] bg-page">
      <header className="flex items-center justify-between border-b border-border bg-card px-8 py-4">
        <p className="text-heading-md font-semibold text-ink">{shop}</p>
        {shown.customer && <p className="text-text-2">{shown.customer}</p>}
      </header>
      <div ref={listRef} className="min-h-0 overflow-y-auto px-8 py-4">
        {shown.lines.map((l) => (
          <div key={l.id} className="flex items-baseline justify-between gap-6 border-b border-border-subtle py-3 text-lg">
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">{l.name}</p>
              <p className="text-sm text-text-2">
                {l.quantity} × {formatMoney(l.price)}
              </p>
            </div>
            <p className="tabular-nums text-ink">{formatMoney(l.net)}</p>
          </div>
        ))}
      </div>
      <footer className="grid gap-1 border-t border-border bg-card px-8 py-5 text-lg">
        <div className="flex justify-between text-text-2">
          <span>Items</span>
          <span className="tabular-nums">{t.itemCount}</span>
        </div>
        {discount > 0 && (
          <div className="flex justify-between text-success-dark">
            <span>You save</span>
            <span className="tabular-nums">{formatMoney(discount)}</span>
          </div>
        )}
        {t.tax > 0 && (
          <div className="flex justify-between text-text-2">
            <span>VAT</span>
            <span className="tabular-nums">{formatMoney(t.tax)}</span>
          </div>
        )}
        <div className="flex items-baseline justify-between">
          <span className="font-semibold text-ink">Total</span>
          {/* eslint-disable-next-line no-restricted-syntax -- read from across the counter; larger than any token */}
          <span className="text-[3rem] font-semibold tabular-nums text-ink">{formatMoney(t.total)}</span>
        </div>
      </footer>
    </div>
  )
}
